import { useState, useEffect, useCallback } from 'react';
import { FileText, ChevronDown, ChevronUp, AlertCircle, Calendar, Users, Shield } from 'lucide-react';
import { leavePolicyAPI } from '../services/api';
import { BlockLoader } from '../components/loading';
import { useAuthStore } from '../stores/authStore';

interface ActiveUser {
  user_id: number;
  name: string;
  emp_code: number;
  project_id: number;
  project_name: string;
  current_policy_id: number;
  current_policy_name: string;
  [key: string]: any;
}

interface LeaveType {
  id: number;
  leaveTypeId: number;
  leaveTypeName: string;
  paidStatus: number;
  leavePeriod: number;
  numLeave: number;
  maxLeave: number;
  carryAllow: number;
  leaveCarried: number;
  active: number;
  noticeReq: number;
  noticeDay: number;
  backdatedAllowed: number;
  backdatedDays: number;
  sexConsider: number;
  [key: string]: any;
}

interface Policy {
  policyId: number;
  policyName: string;
  policyDesc: string;
  active: number;
  activeUserCount: number;
  fyFromMonth: number;
  [key: string]: any;
}

interface PolicyWithDetails {
  policyId: number;
  policyName: string;
  policyDesc: string;
  active: number;
  leaveTypes: LeaveType[];
  employeeCount: number;
}

export default function LeavePolicyPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [policies, setPolicies] = useState<PolicyWithDetails[]>([]);
  const [expandedPolicyId, setExpandedPolicyId] = useState<number | null>(null);
  const user = useAuthStore((state) => state.user);
  const allowedProjectIds = (user?.accessibleProjects || []).map((p) => p.projectId);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Step 1: Fetch all active employees
      const usersResponse = await leavePolicyAPI.getActiveUsers();
      const rawData = usersResponse.data;
      const allUsers: ActiveUser[] = Array.isArray(rawData)
        ? rawData
        : rawData?.data || rawData?.embedded || [];

      // Step 2: Filter by project_id IN user's accessible projects
      const filteredUsers = allowedProjectIds.length > 0
        ? allUsers.filter((u) => u.project_id && allowedProjectIds.includes(u.project_id))
        : allUsers;

      // Step 3: Group by current_policy_id to get unique policy IDs and employee counts
      const policyMap = new Map<number, number>();
      for (const user of filteredUsers) {
        if (user.current_policy_id) {
          policyMap.set(
            user.current_policy_id,
            (policyMap.get(user.current_policy_id) || 0) + 1
          );
        }
      }

      const uniquePolicyIds = Array.from(policyMap.keys());

      if (uniquePolicyIds.length === 0) {
        setPolicies([]);
        setLoading(false);
        return;
      }

      // Step 4: Fetch all policies and leave types in parallel
      const [policiesResponse, ...leaveTypeResponses] = await Promise.all([
        leavePolicyAPI.getAllPolicies(),
        ...uniquePolicyIds.map((id) => leavePolicyAPI.getPolicyLeaveTypes(id)),
      ]);

      const policiesRaw = policiesResponse.data;
      const allPolicies: Policy[] = Array.isArray(policiesRaw)
        ? policiesRaw
        : policiesRaw?.data || [];

      // Step 5: Build policy details with leave types
      const policyDetails: PolicyWithDetails[] = uniquePolicyIds
        .map((policyId, index) => {
          const policy = allPolicies.find((p) => p.policyId === policyId);

          const ltRaw = leaveTypeResponses[index]?.data;
          const leaveTypes: LeaveType[] = Array.isArray(ltRaw)
            ? ltRaw
            : ltRaw?.data || [];

          return {
            policyId,
            policyName: policy?.policyName || `Policy #${policyId}`,
            policyDesc: policy?.policyDesc || '',
            active: policy?.active ?? 1,
            leaveTypes,
            employeeCount: policyMap.get(policyId) || 0,
          };
        })
        .sort((a, b) => b.employeeCount - a.employeeCount);

      setPolicies(policyDetails);
    } catch (err: any) {
      console.error('Failed to fetch leave policy data:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to load leave policy data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleExpand = (policyId: number) => {
    setExpandedPolicyId((prev) => (prev === policyId ? null : policyId));
  };

  const totalEmployees = policies.reduce((sum, p) => sum + p.employeeCount, 0);
  const totalLeaveTypes = new Set(
    policies.flatMap((p) => p.leaveTypes.map((lt) => lt.leaveTypeName))
  ).size;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leave Policy</h1>
          <p className="text-sm text-gray-500 mt-1">
            View leave policies assigned to project employees
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      {!loading && !error && policies.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-50 rounded-lg">
                <Shield className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Policies</p>
                <p className="text-2xl font-bold text-gray-900">{policies.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-green-50 rounded-lg">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Employees Covered</p>
                <p className="text-2xl font-bold text-gray-900">{totalEmployees}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-purple-50 rounded-lg">
                <Calendar className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Leave Types</p>
                <p className="text-2xl font-bold text-gray-900">{totalLeaveTypes}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <BlockLoader label="Loading leave policies..." className="py-20" />
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="h-8 w-8 text-red-400 mx-auto" />
          <p className="mt-2 text-sm text-red-700">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && policies.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <FileText className="h-12 w-12 text-gray-300 mx-auto" />
          <p className="mt-3 text-sm text-gray-500">No leave policies found for this project.</p>
        </div>
      )}

      {/* Policy Cards */}
      {!loading && !error && policies.length > 0 && (
        <div className="space-y-4">
          {policies.map((policy) => {
            const isExpanded = expandedPolicyId === policy.policyId;

            return (
              <div
                key={policy.policyId}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden"
              >
                {/* Policy Header */}
                <button
                  onClick={() => toggleExpand(policy.policyId)}
                  className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="p-2.5 bg-blue-50 rounded-lg flex-shrink-0">
                      <FileText className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 truncate">
                        {policy.policyName}
                      </h3>
                      {policy.policyDesc && (
                        <p className="text-sm text-gray-500 mt-0.5 truncate">
                          {policy.policyDesc}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-1.5">
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <Users className="h-3.5 w-3.5" />
                          {policy.employeeCount} employee{policy.employeeCount !== 1 ? 's' : ''}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <Calendar className="h-3.5 w-3.5" />
                          {policy.leaveTypes.length} leave type{policy.leaveTypes.length !== 1 ? 's' : ''}
                        </span>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            policy.active === 1
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {policy.active === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Leave Types Table */}
                {isExpanded && (
                  <div className="border-t border-gray-100">
                    {policy.leaveTypes.length === 0 ? (
                      <div className="p-6 text-center text-sm text-gray-500">
                        No leave types configured for this policy.
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50/70 border-b border-gray-100">
                              <th className="text-left px-5 py-3 font-semibold text-gray-600">Leave Type</th>
                              <th className="text-center px-5 py-3 font-semibold text-gray-600">Leaves / Period</th>
                              <th className="text-center px-5 py-3 font-semibold text-gray-600">Max Leaves</th>
                              <th className="text-center px-5 py-3 font-semibold text-gray-600">Carry Forward</th>
                              <th className="text-center px-5 py-3 font-semibold text-gray-600">Paid</th>
                              <th className="text-center px-5 py-3 font-semibold text-gray-600">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {policy.leaveTypes.map((lt, idx) => (
                              <tr key={lt.id || idx} className="hover:bg-gray-50 transition-colors">
                                <td className="px-5 py-3 font-medium text-gray-900">
                                  {lt.leaveTypeName || '—'}
                                </td>
                                <td className="px-5 py-3 text-center text-gray-700">
                                  {lt.numLeave ?? '—'}
                                </td>
                                <td className="px-5 py-3 text-center text-gray-700">
                                  {lt.maxLeave ?? '—'}
                                </td>
                                <td className="px-5 py-3 text-center">
                                  {lt.carryAllow === 1 ? (
                                    <span className="text-green-600 font-medium">
                                      Yes{lt.leaveCarried ? ` (max ${lt.leaveCarried})` : ''}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">No</span>
                                  )}
                                </td>
                                <td className="px-5 py-3 text-center">
                                  {lt.paidStatus === 1 ? (
                                    <span className="text-green-600 font-medium">Paid</span>
                                  ) : (
                                    <span className="text-orange-500 font-medium">Unpaid</span>
                                  )}
                                </td>
                                <td className="px-5 py-3 text-center">
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                      lt.active === 1
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-gray-100 text-gray-500'
                                    }`}
                                  >
                                    {lt.active === 1 ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
